import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { TransEditable } from '@/components/i18n/TransEditable';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { WalletBalance } from '@/components/wallet/WalletBalance';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { creatorSpaceApi } from '@/lib/api/creatorSpace';
import { chatAPI, type ChatSessionWithDetails } from '@/lib/api/chat';
import { useGlobalWebSocket } from '@/contexts/GlobalWebSocketContext';
// import { LanguageSwitcher } from '@/components/LanguageSwitcher'; // LanguageSwitcher temporär deaktiviert - für späteren Einsatz vorgehalten
import {
  Home,
  Users,
  MessageCircle,
  User,
  LogOut,
  // Shield, // temporär entfernt mit Admin-Floating-Button
  Heart,
  Zap,
  // Gamepad2, // temporär entfernt mit Games-Feature
  Crown,
  Wallet,
  ShoppingBag,
  LayoutDashboard,
  Trophy
} from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useDesign } from '@/contexts/DesignContext';
import { HStack } from '@/components/core';
import { defaultBrandingTokens } from '@/design-system/tokens/branding';

export function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useTranslation('common');
  const { config } = useDesign();
  const { isConnected, subscribe } = useGlobalWebSocket();
  const [unreadOverride, setUnreadOverride] = useState<number | null>(null);

  // Check if any creators exist
  const { data: creators = [] } = useQuery({
    queryKey: ['creators', 'count'],
    queryFn: () => creatorSpaceApi.getCreators(0, 1), // Only fetch 1 to check if any exist
    enabled: isAuthenticated, // Only check if user is authenticated
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Chat-Sessions für Unread-Indikator laden (geteilt mit ChatList via Query-Key)
  // IMPORTANT: staleTime must match ChatList's staleTime (60s) to prevent refetch conflicts
  const { data: chatSessions = [], refetch: refetchChatSessions } = useQuery({
    queryKey: ['chat-sessions-all'],
    queryFn: async () => {
      // Load both normal and archived sessions for complete unread count
      const [normalRes, archivedRes] = await Promise.all([
        chatAPI.getSessions(0, 100, false),
        chatAPI.getSessions(0, 100, true)
      ]);
      const normal = (normalRes.data || []).map((s) => ({ ...s, is_archived: false as const }));
      const archived = (archivedRes.data || []).map((s) => ({ ...s, is_archived: true as const }));
      return [...normal, ...archived] as Array<ChatSessionWithDetails & { is_archived: boolean }>;
    },
    enabled: isAuthenticated,
    staleTime: 60000, // Consistent with ChatList
  });

  // Only count unread from non-archived sessions
  const unreadChatCount = Array.isArray(chatSessions)
    ? chatSessions.filter((s) => !s.is_archived).reduce((sum, s) => sum + (s.unread_count ?? 0), 0)
    : 0;
  const displayedUnreadChatCount = unreadOverride ?? unreadChatCount;

  // Falls die Sessions aktualisiert wurden, Override zurücksetzen
  useEffect(() => {
    if (unreadOverride !== null) {
      setUnreadOverride(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSessions]);

  // Live-Updates für Unread-Indikator via WebSocket
  useEffect(() => {
    if (!isAuthenticated || !isConnected) return;
    const unsubscribe = subscribe((data: any) => {
      if (
        data?.type === 'unread_count_update' ||
        data?.type === 'message' ||
        data?.type === 'character_message' ||
        data?.type === 'chat_message' ||
        data?.type === 'ai_response' ||
        data?.type === 'message_saved'
      ) {
        // Falls Session-Counts bereits mitkommen, sofortige Anzeige ohne Netzrunde
        if (data?.type === 'unread_count_update' && data?.session_counts && typeof data.session_counts === 'object') {
          try {
            const sum = Object.values<number>(data.session_counts as Record<string, number>).reduce((a, b) => a + (b || 0), 0);
            setUnreadOverride(sum);
          } catch {
            // ignore graceful
          }
        }
        // sanftes Refetch, um nicht zu aggressiv zu pollen
        setTimeout(() => { void refetchChatSessions(); }, 250);
      }
    });
    return () => { unsubscribe?.(); };
  }, [isAuthenticated, isConnected, subscribe, refetchChatSessions]);

  // Bei Tab-Fokus/Visibility Wechsel aktualisieren (failsafe)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        void refetchChatSessions();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [isAuthenticated, refetchChatSessions]);

  const hasCreators = creators.length > 0;
  const siteTitle = config?.branding?.site_title || defaultBrandingTokens.siteName;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { to: '/', key: 'navigation.home', icon: Home, public: true },
    { to: '/characters', key: 'navigation.characters', icon: Users, public: true },
    { to: '/chats', key: 'navigation.chats', icon: MessageCircle, auth: true },
    ...(hasCreators ? [{ to: '/creator-space', key: 'navigation.creators', icon: Crown, auth: true }] : []),
    { to: '/swipe', key: 'navigation.swipe', icon: Heart, auth: true },
    { to: '/matches', key: 'navigation.matches', icon: Zap, auth: true },
    // { to: '/games', key: 'navigation.games', icon: Gamepad2, auth: true }, // Games temporär deaktiviert - für späteren Einsatz vorgehalten
  ];

  // adminLinks entfernt - jetzt in AdminFloatingButton

  return (
    <nav className="hidden md:block fixed top-0 left-0 right-0 z-50 design-glass-desktop-nav">
      <div className="max-w-7xl mx-auto px-token-md sm:px-token-lg lg:px-token-xl">
        <div className="hidden md:flex md:items-center md:justify-between h-16">
          {/* Left Column - Logo */}
          <HStack justify="start" className="flex-shrink-0">
            <Link
              to={isAuthenticated ? "/characters" : "/"}
              className="flex items-center gap-token-sm text-white font-bold text-xl hover:opacity-80 transition-colors"
              style={{ color: 'var(--color-primary-text)' }}
              title={siteTitle}
            >
              {config?.branding?.logo_url ? (
                <img src={config.branding.logo_url} alt={siteTitle} className="h-8 w-auto rounded flex-shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-token-button flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(to right, var(--color-primary-gradient-start), var(--color-accent-text))' }}>
                  <Heart size={18} className="text-white" />
                </div>
              )}
              {/* Site name hides FIRST - only visible at xl (1280px+) */}
              {(config?.branding?.show_site_name !== false) && (
                <span className="hidden xl:inline whitespace-nowrap">{siteTitle}</span>
              )}
            </Link>
          </HStack>

          {/* Navigation Links - centered when labels visible, right-aligned when icons only */}
          <HStack align="center" className="justify-end lg:justify-center gap-token-xs lg:gap-token-sm flex-1 min-w-0">
            {navLinks.map((link) => {
              const Icon = link.icon;
              // Smart active detection: exact match for "/" or prefix match for other routes
              const isActive = link.to === '/'
                ? location.pathname === '/'
                : location.pathname === link.to || location.pathname.startsWith(link.to + '/');

              if (link.auth && !isAuthenticated) return null;
              if (!link.public && !link.auth) return null;
              // Hide Home link for authenticated users
              if (link.to === '/' && isAuthenticated) return null;

              return (
                <Link
                  key={link.to}
                  to={link.to}
                  title={t(link.key)}
                  className={`flex items-center gap-token-sm px-token-sm lg:px-token-sm py-2.5 rounded-token-button transition-all duration-200 relative z-10 overflow-visible ${
                    isActive
                      ? 'font-medium'
                      : 'hover:bg-white/5'
                  }`}
                  style={{
                    color: isActive ? 'var(--color-nav-icon-active)' : 'var(--color-nav-text)',
                    backgroundColor: isActive ? 'color-mix(in srgb, var(--color-nav-icon-active) 20%, transparent)' : undefined
                  }}
                >
                  <Icon size={18} className="flex-shrink-0" />
                  {/* Menu labels visible at lg (1024px+) */}
                  <span className="hidden lg:inline whitespace-nowrap">
                    <TransEditable namespace="common" i18nKey={link.key} />
                  </span>
                  {link.to === '/chats' && displayedUnreadChatCount > 0 && (
                    <span
                      className="absolute -top-1.5 -right-1 translate-x-[30%] h-5 min-w-5 px-1.5 rounded-full flex items-center justify-center text-xs font-medium border-0"
                      style={{
                        background: 'var(--color-nav-badge-bg)',
                        color: 'var(--color-nav-badge-text)',
                      }}
                    >
                      {displayedUnreadChatCount > 99 ? '99+' : displayedUnreadChatCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </HStack>

          {/* Right Column - User Menu */}
          <HStack align="center" className="justify-end gap-token-sm lg:gap-token-sm flex-shrink-0">
            {/* <LanguageSwitcher /> */} {/* LanguageSwitcher temporär deaktiviert - für späteren Einsatz vorgehalten */}

            {isAuthenticated ? (
              <HStack align="center" gap="sm">
                {/* Wallet Balance */}
                <WalletBalance />

                {/* Notification Bell */}
                <NotificationBell />

                {/* User Menu (Dropdown) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="glass"
                      size="sm"
                      className="p-token-xs text-muted hover:text-white"
                    >
                      <User size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="glass-dropdown-panel rounded-token-card min-w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center" style={location.pathname === '/dashboard' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 10%, transparent)' } : undefined}>
                        <LayoutDashboard size={14} className="mr-token-sm" />
                        <TransEditable namespace="common" i18nKey="navigation.dashboard" />
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center" style={location.pathname === '/profile' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 10%, transparent)' } : undefined}>
                        <User size={14} className="mr-token-sm" />
                        <TransEditable namespace="common" i18nKey="navigation.profile" />
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/wallet" className="flex items-center" style={location.pathname === '/wallet' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 10%, transparent)' } : undefined}>
                        <Wallet size={14} className="mr-token-sm" />
                        <TransEditable namespace="common" i18nKey="navigation.wallet" />
                      </Link>
                    </DropdownMenuItem>
                    {user?.role === 'partner_admin' && (
                      <DropdownMenuItem asChild>
                        <Link to="/partner/billing" className="flex items-center" style={location.pathname.startsWith('/partner') ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 10%, transparent)' } : undefined}>
                          <LayoutDashboard size={14} className="mr-token-sm" />
                          <TransEditable namespace="common" i18nKey="navigation.partnerBilling" />
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/subscription" className="flex items-center" style={location.pathname === '/subscription' || location.pathname === '/my-subscriptions' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 10%, transparent)' } : undefined}>
                        <Crown size={14} className="mr-token-sm" />
                        <TransEditable namespace="common" i18nKey="navigation.mySubscriptions" />
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/shop" className="flex items-center" style={location.pathname === '/shop' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 10%, transparent)' } : undefined}>
                        <ShoppingBag size={14} className="mr-token-sm" />
                        <TransEditable namespace="common" i18nKey="navigation.shop" />
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/daily-challenges" className="flex items-center" style={location.pathname === '/daily-challenges' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 10%, transparent)' } : undefined}>
                        <Trophy size={14} className="mr-token-sm" />
                        <TransEditable namespace="common" i18nKey="navigation.dailyChallenges" />
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/achievements" className="flex items-center" style={location.pathname === '/achievements' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 10%, transparent)' } : undefined}>
                        <Trophy size={14} className="mr-token-sm" />
                        <TransEditable namespace="common" i18nKey="navigation.achievements" />
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/favorites" className="flex items-center" style={location.pathname === '/favorites' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 10%, transparent)' } : undefined}>
                        <Heart size={14} className="mr-token-sm" />
                        <TransEditable namespace="common" i18nKey="navigation.favorites" />
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} style={{ color: 'var(--color-error)' }} className="focus:text-error">
                      <LogOut size={14} className="mr-token-sm" />
                      <TransEditable namespace="common" i18nKey="navigation.logout" />
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </HStack>
            ) : (
              <HStack align="center" gap="sm">
                <Link to="/auth?tab=login">
                  <Button size="sm" variant="ghost" className="text-muted hover:text-white">
                    <TransEditable namespace="common" i18nKey="navigation.login" />
                  </Button>
                </Link>
                <Link to="/auth?tab=register">
                  <Button size="sm" className="design-gradient-primary">
                    <TransEditable namespace="common" i18nKey="navigation.register" />
                  </Button>
                </Link>
              </HStack>
            )}
          </HStack>
        </div>

        {/* Mobile Layout - Ausgeblendet, da separater MobileHeader verwendet wird */}
        {/* <div className="md:hidden flex justify-between items-center h-16">
          // Mobile Layout wurde zu MobileHeader.tsx verschoben
        </div> */}
      </div>
    </nav>
  );
}
